'use client';

import React from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { color, spacing, borderRadius, breakpoints } from '@/theme/tokens';
import IconChevronDown from '@/assets/icons/IconChevronDown';
import { CountryWithBrokers } from './types';
import { AuthorizedBrokersColorsPalette } from '@/components/authorizedBrokers/types';
import ContactLocation from '@/components/contacts/ContactLocation';

interface BrokerSectionAccordionProps {
    country: CountryWithBrokers;
    expanded: boolean;
    onChange: (countryId: string, isExpanded: boolean) => void;
    colorsPalette: AuthorizedBrokersColorsPalette;
    // When true, two columns start from breakpoints.md; when false (default), from 864px
    twoColumnsFromMd?: boolean;
}

// Shared transition timing for all animated properties
const TRANSITION_DURATION = '300ms';
const TRANSITION_EASING = 'ease-in-out';
const TRANSITION = `${TRANSITION_DURATION} ${TRANSITION_EASING}`;

// Large tablet breakpoint
const LARGE_TABLET_BREAKPOINT = '864px';

const ContactsGrid = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'twoColumnsFromMd',
}) <{ twoColumnsFromMd?: boolean }>`
    display: grid;
    grid-template-columns: 1fr;
    
    @media (min-width: ${(props) => props.twoColumnsFromMd ? breakpoints.md : LARGE_TABLET_BREAKPOINT}) {
        grid-template-columns: 1fr 1fr;
        column-gap: ${spacing[32]};
    }
`;

const ContactLocationWrapper = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'twoColumnsFromMd',
}) <{ twoColumnsFromMd?: boolean }>`
    padding-top: ${spacing[24]};
    padding-bottom: ${spacing[24]};
    position: relative;

    @media (max-width: ${breakpoints.md}) {
        padding-bottom: ${spacing[20]};
        padding-top: ${spacing[20]};
    }

    @media (max-width: ${breakpoints.sm}) {
        padding-bottom: ${spacing[16]};
        padding-top: ${spacing[16]};
    }

    /* First item: no top padding */
    &:first-of-type {
        padding-top: 0;
    }

    /* Last item: no bottom padding */
    &:last-child {
        padding-bottom: 0;
    }

    /* Mobile: border on every item except last */
    border-bottom: 1px solid ${color.black[10]};
    
    &:last-child {
        border-bottom: none;
    }

    /* Two columns layout styles */
    @media (min-width: ${(props) => props.twoColumnsFromMd ? breakpoints.md : LARGE_TABLET_BREAKPOINT}) {
        border-bottom: none;
        
        /* First row: no top padding for both items (1st and 2nd) */
        &:nth-of-type(2) {
            padding-top: 0;
        }

        /*
         * Border strategy for 2-column grid:
         * - Only left column items (odd) render a full-width pseudo-element border
         * - Border spans both columns using calc(200% + gap)
         * - Last row detection: remove border if item is either:
         *   1) last-child and odd (single item in last row)
         *   2) second-to-last and odd (left item of complete last row)
         */
        &:nth-child(odd)::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: calc(200% + ${spacing[32]});
            height: 1px;
            background-color: ${color.black[10]};
        }

        /* Last row border removal */
        &:last-child:nth-child(odd)::after {
            display: none;
        }

        &:nth-last-child(2):nth-child(odd)::after {
            display: none;
        }

        /* Last row items: no bottom padding */
        &:nth-last-child(2):nth-child(odd) ~ & {
            padding-bottom: 0;
        }
        
        &:nth-last-child(2):nth-child(odd) {
            padding-bottom: 0;
        }
    }
`;

export default function BrokerSectionAccordion({
    country,
    expanded,
    onChange,
    colorsPalette,
    twoColumnsFromMd = false,
}: BrokerSectionAccordionProps) {
    const handleChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
        onChange(country.id, isExpanded);
    };

    return (
        <Accordion
            expanded={expanded}
            onChange={handleChange}
            slotProps={{
                transition: {
                    timeout: 300,
                    easing: TRANSITION_EASING,
                },
            }}
            sx={{
                borderRadius: `${borderRadius[8]} !important`,
                boxShadow: 'none',
                marginBottom: spacing[8],
                marginTop: spacing[0],
                backgroundColor: colorsPalette.accordionBackgroundColor,
                overflow: 'hidden',
                border: `1px solid ${color.white[20]}`,
                transition: `background-color ${TRANSITION}`,
                '&.Mui-expanded': {
                    marginTop: spacing[0],
                    marginBottom: spacing[8],
                    backgroundColor: colorsPalette.accordionExpandedBackgroundColor,
                },
                '&:before': {
                    display: 'none',
                },
                '&:last-child': {
                    marginBottom: 0,
                },
                '&:has(.MuiAccordionSummary-root.Mui-focusVisible)': {
                    outline: `2px solid ${color.gray[80]}`,
                    outlineOffset: '2px',
                },
            }}
        >
            <AccordionSummary
                expandIcon={
                    <IconChevronDown
                        color={expanded ? colorsPalette.iconExpandedColor : colorsPalette.iconColor}
                        width={24}
                        height={24}
                    />
                }
                sx={{
                    backgroundColor: expanded
                        ? colorsPalette.accordionExpandedBackgroundColor
                        : colorsPalette.accordionBackgroundColor,
                    padding: {
                        xs: `${spacing[16]} ${spacing[12]} ${spacing[16]} ${spacing[16]}`,
                        sm: `${spacing[16]} ${spacing[16]} ${spacing[16]} ${spacing[20]}`,
                        md: `${spacing[16]} ${spacing[16]} ${spacing[16]} ${spacing[24]}`
                    },
                    minHeight: 'auto',
                    color: expanded
                        ? colorsPalette.accordionExpandedTextColor
                        : colorsPalette.accordionTextColor,
                    transition: `background-color ${TRANSITION}, color ${TRANSITION}`,
                    '&.Mui-expanded': {
                        minHeight: 'auto',
                    },
                    '&.Mui-focusVisible': {
                        backgroundColor: expanded
                            ? colorsPalette.accordionExpandedBackgroundColor
                            : colorsPalette.accordionBackgroundColor,
                        outline: 'none',
                    },
                    '& .MuiAccordionSummary-content': {
                        margin: 0,
                        paddingRight: spacing[16],
                    },
                    '& .MuiAccordionSummary-content.Mui-expanded': {
                        margin: 0,
                    },
                    '& .MuiAccordionSummary-expandIconWrapper': {
                        transition: `transform ${TRANSITION}`,
                    },
                    'svg path': {
                        transition: `fill ${TRANSITION}, stroke ${TRANSITION}`,
                    },
                }}
            >
                <Typography variant="heading1bold" component="span">
                    {country.name}
                </Typography>
            </AccordionSummary>

            <AccordionDetails
                sx={{
                    backgroundColor: expanded
                        ? colorsPalette.accordionExpandedBackgroundColor
                        : colorsPalette.accordionBackgroundColor,
                    padding: { xs: spacing[16], sm: spacing[20], md: spacing[24] },
                    paddingTop: { xs: spacing[0], sm: spacing[0], md: spacing[0] },
                    paddingBottom: { xs: spacing[16], sm: spacing[20], md: spacing[24] },
                    color: expanded
                        ? colorsPalette.accordionExpandedTextColor
                        : colorsPalette.accordionTextColor,
                    transition: `background-color ${TRANSITION}, color ${TRANSITION}`,
                }}
            >
                <ContactsGrid twoColumnsFromMd={twoColumnsFromMd}>
                    {country.brokers.map((broker) => (
                        <ContactLocationWrapper
                            key={broker.id}
                            twoColumnsFromMd={twoColumnsFromMd}
                        >
                            <ContactLocation
                                id={broker.id}
                                name={broker.name}
                                address={broker.address}
                                mapUrl={broker.mapUrl}
                                phone={broker.phone}
                                email={broker.email}
                                website={broker.website}
                                manager={broker.manager}
                                company={broker.company}
                            />
                        </ContactLocationWrapper>
                    ))}
                </ContactsGrid>
            </AccordionDetails>
        </Accordion>
    );
}
